import classNames from 'classnames'
import React from 'react'

import { UI2EntityPreviewCard } from 'browser/components/ui2/display/ui2-entity-preview-card/ui2-entity-preview-card'
import {
  UI2PulseHighlight,
  IUI2PulseHighlightRef,
} from 'browser/components/ui2/behavior/pulse-highlight/pulse-highlight'
import './_entity-search-multi-chips.scss'

export interface MultiSelectValue {
  entityId: string
  displayName?: string
}

interface IEntitySearchMultiChipsProps {
  values: MultiSelectValue[]
  isDisabled?: boolean
  isReadOnly?: boolean
  entityEditSchemaPath?: string
  entityReadOnlySchemaPath?: string
  onMorePress: () => void
  onModalClose?: () => void
}

export interface IEntitySearchMultiChipsHandle {
  pulse: () => void
}

const MAX_VISIBLE_CHIPS = 5

// Inline styles applied to the UI2PulseHighlight wrapper so it becomes the
// flex child with chip sizing, overriding the pulse highlight's default
// width:100%. The chip div inside then fills the wrapper with flex:1.
const CHIP_PULSE_WRAPPER_STYLE: React.CSSProperties = {
  display: 'inline-flex',
  flex: '1 1 calc(50% - 3px)',
  maxWidth: 'calc(50% - 3px)',
  minWidth: 0,
  width: 'auto',
  borderRadius: 4,
}

/**
 * Compact chip row for multi-select mode. Shows up to 5 display-name chips
 * then a "+N more" chip that opens the full selection list.
 *
 * Chip interactivity (open edit/read-only sheet) is resolved by the card
 * itself via resolveClick — the chip component does not duplicate that logic.
 *
 * Exposes a `pulse()` method (via ref) that fires each chip's individual ring
 * so callers can highlight the chips rather than the surrounding container.
 */
export const EntitySearchMultiChips = React.forwardRef<
  IEntitySearchMultiChipsHandle,
  IEntitySearchMultiChipsProps
>(function EntitySearchMultiChips(props, ref) {
  const {
    values,
    isDisabled,
    isReadOnly,
    entityEditSchemaPath,
    entityReadOnlySchemaPath,
    onMorePress,
    onModalClose,
  } = props

  const chipPulseRefs = React.useRef<(IUI2PulseHighlightRef | null)[]>([])
  const visibleValues = values.slice(0, MAX_VISIBLE_CHIPS)
  const overflowCount = values.length - MAX_VISIBLE_CHIPS

  React.useImperativeHandle(ref, () => ({
    pulse: () => {
      chipPulseRefs.current.forEach((r) => r?.pulse())
    },
  }))

  return (
    <div className='ui2-entitySearchMultiChips'>
      {visibleValues.map((item, i) => (
        <UI2PulseHighlight
          key={item.entityId}
          ref={(el) => { chipPulseRefs.current[i] = el }}
          style={CHIP_PULSE_WRAPPER_STYLE}
        >
          <div
            className='ui2-entitySearchMultiChips-chip'
            title={item.displayName ?? item.entityId}
          >
            <UI2EntityPreviewCard
              value={{ entityId: item.entityId }}
              title={item.displayName}
              isChipMode={true}
              showTypeStripe={true}
              isDisabled={isDisabled}
              isReadOnly={isReadOnly}
              entityEditSchemaPath={entityEditSchemaPath}
              entityReadOnlySchemaPath={entityReadOnlySchemaPath}
              onModalClose={onModalClose}
            />
          </div>
        </UI2PulseHighlight>
      ))}
      {overflowCount > 0 && (
        <button
          className={classNames(
            'ui2-entitySearchMultiChips-chip',
            'ui2-entitySearchMultiChips-chip--more'
          )}
          onClick={onMorePress}
          type='button'
        >
          <span className='ui2-entitySearchMultiChips-chipLabel'>
            {`+${overflowCount} more`}
          </span>
        </button>
      )}
    </div>
  )
})
EntitySearchMultiChips.displayName = 'EntitySearchMultiChips'
